import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ClipboardText, Plus, PencilSimple, Trash, X, CircleNotch } from "@phosphor-icons/react";

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none";
const QUESTION_TYPES = ["boolean", "text"];
const blankQuestion = () => ({ id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, text: "", type: "boolean" });
const blankForm = () => ({ name: "", room_type: "", job_type: "", questions: [blankQuestion()] });

export default function ChecklistTemplates() {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // {edit, form}
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    async function load() {
        setLoading(true);
        try { const { data } = await endpoints.checklistTemplates(); setList(data || []); }
        catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openNew() { setModal({ edit: null, form: blankForm() }); setErr(""); }
    function openEdit(t) {
        setModal({ edit: t, form: {
            name: t.name || "", room_type: t.room_type || "", job_type: t.job_type || "",
            questions: (t.questions?.length ? t.questions : [blankQuestion()]).map((q) => ({ ...q })),
        } });
        setErr("");
    }

    function updateField(k, v) { setModal((m) => ({ ...m, form: { ...m.form, [k]: v } })); }
    function updateQuestion(i, k, v) {
        setModal((m) => {
            const questions = m.form.questions.map((q, qi) => qi === i ? { ...q, [k]: v } : q);
            return { ...m, form: { ...m.form, questions } };
        });
    }
    function addQuestion() { setModal((m) => ({ ...m, form: { ...m.form, questions: [...m.form.questions, blankQuestion()] } })); }
    function removeQuestion(i) { setModal((m) => ({ ...m, form: { ...m.form, questions: m.form.questions.filter((_, qi) => qi !== i) } })); }

    async function save() {
        const f = modal.form;
        if (!f.name.trim()) { setErr("Template name is required"); return; }
        const questions = f.questions.filter((q) => q.text.trim());
        if (questions.length === 0) { setErr("Add at least one question"); return; }
        setSaving(true); setErr("");
        const payload = { name: f.name.trim(), room_type: f.room_type.trim() || null, job_type: f.job_type.trim() || null, questions };
        try {
            if (modal.edit) await endpoints.updateChecklistTemplate(modal.edit.id, payload);
            else await endpoints.createChecklistTemplate(payload);
            setModal(null);
            await load();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setSaving(false); }
    }

    async function remove(t) {
        if (!window.confirm(`Delete "${t.name}"? Mobile will stop seeing this template on its next pull.`)) return;
        try { await endpoints.deleteChecklistTemplate(t.id); await load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="max-w-[1000px] mx-auto px-6 py-8 space-y-6" data-testid="checklist-templates-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Field Gateway</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <ClipboardText size={28} weight="bold" className="text-[var(--rom-orange)]" /> Checklist Templates
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Room/job checklists Mobile pulls for on-site documentation.</p>
                </div>
                {isManage && (
                    <button className="btn btn-primary" onClick={openNew} data-testid="add-checklist-template-btn">
                        <Plus size={14} weight="bold" /> Add template
                    </button>
                )}
            </header>

            {err && !modal && <div className="text-sm text-red-600">{err}</div>}

            {loading ? (
                <div className="text-center py-10 text-sm text-[var(--rom-text-3)]"><CircleNotch size={20} className="animate-spin mx-auto mb-2" /> Loading…</div>
            ) : list.length === 0 ? (
                <div className="rom-card"><div className="rom-card-body text-center py-10 text-sm text-[var(--rom-text-3)]" data-testid="checklist-templates-empty">No checklist templates yet.</div></div>
            ) : (
                <div className="space-y-3">
                    {list.map((t) => (
                        <div key={t.id} className="rom-card" data-testid={`checklist-template-${t.id}`}>
                            <div className="rom-card-head">
                                <h3 className="flex items-center gap-2 flex-wrap">
                                    {t.name}
                                    {t.room_type && <span className="chip chip-navy text-[10px] capitalize">{t.room_type}</span>}
                                    {t.job_type && <span className="chip chip-orange text-[10px] capitalize">{t.job_type}</span>}
                                    {!t.room_type && !t.job_type && <span className="chip chip-green text-[10px]">Default (all)</span>}
                                </h3>
                                {isManage && (
                                    <div className="flex gap-1">
                                        <button onClick={() => openEdit(t)} data-testid={`edit-checklist-template-${t.id}`} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-slate-100" title="Edit"><PencilSimple size={15} /></button>
                                        <button onClick={() => remove(t)} data-testid={`delete-checklist-template-${t.id}`} className="p-1.5 rounded text-red-500 hover:bg-red-50" title="Delete"><Trash size={15} /></button>
                                    </div>
                                )}
                            </div>
                            <div className="rom-card-body">
                                <ul className="space-y-1">
                                    {(t.questions || []).map((q, i) => (
                                        <li key={q.id || i} className="text-sm flex items-center gap-2">
                                            <span className="text-[var(--rom-text-3)] text-xs w-16 shrink-0 uppercase font-semibold">{q.type}</span>
                                            <span>{q.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && setModal(null)}>
                    <div className="bg-white rounded-lg w-full max-w-xl shadow-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="checklist-template-modal">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rom-border)]">
                            <h3 className="font-display font-bold text-lg">{modal.edit ? "Edit template" : "Add template"}</h3>
                            <button onClick={() => setModal(null)} className="p-1 text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-1">
                                    <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Name *</label>
                                    <input data-testid="ct-name" value={modal.form.name} onChange={(e) => updateField("name", e.target.value)} className={inputCls} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Room type (optional)</label>
                                    <input data-testid="ct-room-type" value={modal.form.room_type} onChange={(e) => updateField("room_type", e.target.value)} placeholder="e.g. bathroom" className={inputCls} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Job type (optional)</label>
                                    <input data-testid="ct-job-type" value={modal.form.job_type} onChange={(e) => updateField("job_type", e.target.value)} placeholder="e.g. water" className={inputCls} />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-xs font-semibold text-[var(--rom-text-2)]">Questions</label>
                                    <button onClick={addQuestion} className="btn btn-ghost text-xs" data-testid="ct-add-question"><Plus size={12} weight="bold" /> Add question</button>
                                </div>
                                <div className="space-y-2">
                                    {modal.form.questions.map((q, i) => (
                                        <div key={q.id || i} className="flex items-center gap-2" data-testid={`ct-question-row-${i}`}>
                                            <input data-testid={`ct-question-text-${i}`} value={q.text} onChange={(e) => updateQuestion(i, "text", e.target.value)} placeholder="Question text" className={`${inputCls} flex-1`} />
                                            <select data-testid={`ct-question-type-${i}`} value={q.type} onChange={(e) => updateQuestion(i, "type", e.target.value)} className="select w-28">
                                                {QUESTION_TYPES.map((qt) => <option key={qt} value={qt}>{qt}</option>)}
                                            </select>
                                            <button onClick={() => removeQuestion(i)} data-testid={`ct-remove-question-${i}`} className="p-1.5 rounded text-red-500 hover:bg-red-50 shrink-0"><Trash size={14} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {err && <div className="px-5 pb-2 text-sm text-red-600" data-testid="ct-error">{err}</div>}
                        <div className="px-5 py-4 border-t border-[var(--rom-border)] flex justify-end gap-2">
                            <button className="btn btn-ghost" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
                            <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="save-checklist-template-btn">
                                {saving ? <CircleNotch size={14} className="animate-spin" /> : null} Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
